// A podcast episode's or an audiobook's player: the browser's own audio
// controls, cued at where you were, with a speed control beside them — the
// setting people change most. An episode plays straight from the show's host;
// an audiobook from this server, through a signed link (see `renew`).
import type { MediaCallbacks, MediaPlayer } from './media.ts';

const SPEED_KEY = 'nexus_podcast_speed';
const SPEEDS = [0.8, 1, 1.2, 1.5, 1.75, 2, 2.5];

function savedSpeed(): number {
  try {
    const speed = Number(localStorage.getItem(SPEED_KEY));
    return SPEEDS.includes(speed) ? speed : 1;
  } catch {
    return 1;
  }
}

export interface AudioSource {
  /** A fresh address for the audio, for when the one given stops working —
   *  an audiobook's signed link runs out after a day (or on a server restart),
   *  and a tab can easily be left open longer than that. */
  renew?: () => Promise<string>;
}

// Renewing at most this often, so a file that is really broken doesn't loop.
const RENEW_EVERY_MS = 60_000;

export function openAudio(
  frame: HTMLElement,
  src: string,
  start: number,
  { onPlayingChange }: MediaCallbacks = {},
  { renew }: AudioSource = {},
): MediaPlayer {
  const audio = document.createElement('audio');
  audio.controls = true;
  audio.preload = 'metadata';
  audio.src = src;
  audio.playbackRate = savedSpeed();

  const speed = document.createElement('select');
  speed.className = 'btn-sm media-speed';
  speed.title = 'Playback speed';
  speed.setAttribute('aria-label', 'Playback speed');
  for (const value of SPEEDS) speed.append(new Option(`${value}×`, String(value), false, value === audio.playbackRate));
  speed.addEventListener('change', () => {
    audio.playbackRate = Number(speed.value);
    try {
      localStorage.setItem(SPEED_KEY, speed.value);
    } catch {
      // Not remembered, but still applied.
    }
  });

  let started = false;
  const listeners = new AbortController();
  const { signal } = listeners;
  // Cued once the browser knows the length, which is when it can seek.
  if (start > 0) audio.addEventListener('loadedmetadata', () => { if (!started) audio.currentTime = start; }, { signal, once: true });
  let playing = false;
  audio.addEventListener('play', () => { started = true; playing = true; onPlayingChange?.(true); }, { signal });
  audio.addEventListener('pause', () => { playing = false; onPlayingChange?.(false); }, { signal });
  audio.addEventListener('ended', () => onPlayingChange?.(false), { signal });
  // The browser's own menu can change the speed too; the select follows it.
  audio.addEventListener('ratechange', () => { speed.value = String(audio.playbackRate); }, { signal });

  let renewedAt = 0;
  audio.addEventListener('error', async () => {
    if (!renew || Date.now() - renewedAt < RENEW_EVERY_MS) return;
    renewedAt = Date.now();
    // Carried on from the same moment, playing if it was.
    const at = started ? audio.currentTime : start;
    const resume = playing;
    let fresh: string;
    try {
      fresh = await renew();
    } catch {
      return;
    }
    if (signal.aborted) return;
    audio.addEventListener('loadedmetadata', () => {
      audio.currentTime = at;
      if (resume) void audio.play().catch(() => {});
    }, { signal, once: true });
    audio.src = fresh;
  }, { signal });

  frame.replaceChildren(audio, speed);

  return {
    seek(seconds) {
      audio.currentTime = seconds;
      void audio.play().catch(() => {});
    },
    time: () => (audio.readyState > 0 ? audio.currentTime : null),
    duration: () => (Number.isFinite(audio.duration) && audio.duration > 0 ? audio.duration : null),
    started: () => started,
    close() {
      listeners.abort();
      audio.pause();
      audio.removeAttribute('src');
      audio.load();
    },
  };
}
